/**
 * Internal Hook: useSettingsMutation
 *
 * Mutation for saving profile settings to backend.
 * Following the pattern from ScheduledMeetingsContext.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createRetryFn } from '../../../config/react-query';
import { useApiManager } from '../../../hooks';
import { useUser } from '../../UserContext';
import { useUserPermissions } from '../../UserPermissionsContext';
import { SETTINGS_QUERY_KEYS } from '../helpers';
import type { ProfileData } from '../types';

export const useSettingsMutation = () => {
  const apiManager = useApiManager();
  const { isSuccess: isUserPermissionsReady } = useUserPermissions();
  const { idToken } = useUser();
  const queryClient = useQueryClient();

  return useMutation<ProfileData, Error, Partial<ProfileData>>({
    mutationFn: async (patch: Partial<ProfileData>) => {
      if (!(apiManager && idToken && isUserPermissionsReady)) {
        console.warn(
          '[SettingsMutation] API Manager not initialized or no token, skipping backend save',
        );
        // Optimistic update only: fold the patch into what we already have so a
        // partial save cannot drop cached fields it never intended to touch.
        const cached = queryClient.getQueryData<ProfileData>(SETTINGS_QUERY_KEYS.settings);
        return { displayName: '', gravatarEmail: '', ...cached, ...patch };
      }

      // Ensure apiManager has the latest token before making the call
      console.log(
        '[SettingsMutation] Setting token, idToken preview:',
        `${idToken.substring(0, 30)}...`,
      );
      apiManager.setAuthToken(idToken);
      console.log(
        '[SettingsMutation] Token set, current token:',
        `${apiManager.getCurrentToken()?.substring(0, 30)}...`,
      );

      // Cache the merged document the server now holds, not just the patch.
      return await apiManager.profile.storeProfileData(patch);
    },
    onSuccess: (data) => {
      // Update the cache with new data
      queryClient.setQueryData(SETTINGS_QUERY_KEYS.settings, data);
      console.log('[SettingsContext] Profile settings saved successfully');
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.warn(`[SettingsContext] Profile API failed: ${errorMessage}`);
      console.warn('Settings will be saved locally and not synchronized across devices');
    },
    retry: createRetryFn({ maxRetries: 2 }),
  });
};
